// Anmelde-Session-Helfer. Die Session wird zur Laufzeit in sessionStorage
// gehalten UND persistent in einem Cookie gespeichert, dessen Ablaufdatum
// exakt dem `exp` des JWT entspricht. Dadurch bleiben Sitzungen nach einem
// Browser-Neustart und in der installierten PWA erhalten – bis der Token
// tatsaechlich ablaeuft (dann wird alles verworfen und neu angemeldet).
const KEYS = ["mag_token", "mag_mode", "mag_root_permid", "mag_user", "mag_user_id", "mag_selected", "mag_role"];
const COOKIE = "mws_session";

export function decodeJwt(token) {
  try {
    const part = token.split(".")[1];
    const b64 = part.replace(/-/g, "+").replace(/_/g, "/").padEnd(Math.ceil(part.length / 4) * 4, "=");
    return JSON.parse(decodeURIComponent(escape(atob(b64))));
  } catch {
    return {};
  }
}

function tokenExp(token) {
  const exp = decodeJwt(token).exp;
  return Number.isFinite(exp) && exp > 0 ? exp : null;
}

function isExpired(token) {
  const exp = tokenExp(token);
  return exp === null || exp <= Math.floor(Date.now() / 1000);
}

function readCookie(name) {
  const prefix = `${name}=`;
  const row = document.cookie.split(";").map((x) => x.trim()).find((x) => x.startsWith(prefix));
  return row ? decodeURIComponent(row.slice(prefix.length)) : null;
}

function writeCookie(name, value, expSeconds) {
  // Expires bewusst aus dem Token-exp abgeleitet (nicht aus einer festen TTL).
  const expires = new Date(expSeconds * 1000).toUTCString();
  document.cookie = [
    `${name}=${encodeURIComponent(value)}`,
    `Expires=${expires}`,
    "Path=/",
    "SameSite=Lax",
    "Secure",
  ].join("; ");
}

function deleteCookie(name) {
  document.cookie = `${name}=; Max-Age=0; Expires=Thu, 01 Jan 1970 00:00:00 GMT; Path=/; SameSite=Lax; Secure`;
}

function persist(s) {
  const exp = tokenExp(s.token);
  if (exp === null || exp <= Math.floor(Date.now() / 1000)) {
    deleteCookie(COOKIE);
    return;
  }
  const data = JSON.stringify({
    token: s.token,
    mode: s.mode || "prod",
    root_permid: s.root_permid || "",
    user_name: s.user_name || "",
    user_id: s.user_id || "",
  });
  writeCookie(COOKIE, data, exp);
}

// Nach Neustart/PWA ist sessionStorage leer -> aus dem persistenten Cookie
// wiederherstellen, damit die laufenden Leser von sessionStorage weiter greifen.
function hydrateFromCookie() {
  const raw = readCookie(COOKIE);
  if (!raw) return null;
  let data;
  try { data = JSON.parse(raw); } catch { deleteCookie(COOKIE); return null; }
  if (!data || !data.token || isExpired(data.token)) { deleteCookie(COOKIE); return null; }
  sessionStorage.setItem("mag_token", data.token);
  sessionStorage.setItem("mag_mode", data.mode || "prod");
  sessionStorage.setItem("mag_root_permid", data.root_permid || "");
  sessionStorage.setItem("mag_user", data.user_name || "");
  sessionStorage.setItem("mag_user_id", data.user_id || "");
  return data;
}

export function readMagSession() {
  let token = sessionStorage.getItem("mag_token");
  if (!token) token = hydrateFromCookie()?.token || null;
  if (!token) return null;
  if (isExpired(token)) {
    clearMagSession();
    return null;
  }
  const claims = decodeJwt(token);
  return {
    token,
    mode: sessionStorage.getItem("mag_mode") || "prod",
    root_permid: sessionStorage.getItem("mag_root_permid") || "",
    user_name: sessionStorage.getItem("mag_user") || claims.user_name || claims.email || "",
    user_id: sessionStorage.getItem("mag_user_id") || claims.user_id || claims.email || "",
  };
}

export function writeMagSession(s) {
  sessionStorage.setItem("mag_token", s.token);
  sessionStorage.setItem("mag_mode", s.mode || "prod");
  sessionStorage.setItem("mag_root_permid", s.root_permid || "");
  sessionStorage.setItem("mag_user", s.user_name || "");
  sessionStorage.setItem("mag_user_id", s.user_id || "");
  sessionStorage.removeItem("mag_selected");
  persist(s);
}

export function clearMagSession() {
  KEYS.forEach((k) => sessionStorage.removeItem(k));
  deleteCookie(COOKIE);
}

// Gleitender Login: nur das App-JWT austauschen (Ablauf = neues exp), uebrige
// Sitzungsfelder beibehalten. Wird vom api-Response-Interceptor aufgerufen.
export function refreshMagToken(token) {
  if (!token || isExpired(token)) return;
  sessionStorage.setItem("mag_token", token);
  persist({
    token,
    mode: sessionStorage.getItem("mag_mode") || "prod",
    root_permid: sessionStorage.getItem("mag_root_permid") || "",
    user_name: sessionStorage.getItem("mag_user") || "",
    user_id: sessionStorage.getItem("mag_user_id") || "",
  });
}

export function initials(name) {
  if (!name) return "?";
  const clean = name.replace(/@.*/, "");
  const parts = clean.split(/[.\-_ ]+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return clean.slice(0, 2).toUpperCase();
}
